import { useEffect, useRef, useState } from 'react';

import { useApi, useVisualization } from '@/context';
import { useAutoSave, type PersistenceState } from '@/hooks/use-auto-save';
import { notifyWorkflowSaveRefused } from '@/lib/workflow-save-error';
import { ApiError } from '@/services';
import type { ISizingStateDTO } from '@/visualization/domain/sizing/types';
import { CarmViewService } from '@/visualization/services/carm-view-service';
import { SizingStateService } from '@/visualization/services/sizing/sizing-state-service';

const WORKFLOW_STATE_KEY = 'sizing';

// ============================================================================
// Types
// ============================================================================

export interface UseSizingPersistenceOptions {
    caseId: string;
}

export type UseSizingPersistenceResult = PersistenceState;

// ============================================================================
// Hook
// ============================================================================

export function useSizingPersistence(options: UseSizingPersistenceOptions): UseSizingPersistenceResult {
    const { caseId } = options;
    const api = useApi();
    const { getServiceManager } = useVisualization();
    const isRestoredRef = useRef(false);
    const [restoreError, setRestoreError] = useState<Error | null>(null);

    // Resolve services from the ServiceManager
    const sm = getServiceManager();
    const sizingService = sm.getService<SizingStateService>(SizingStateService.SERVICE_NAME);
    const carmViewService = sm.getService<CarmViewService>(CarmViewService.SERVICE_NAME);

    // ── Auto-save setup ──
    const autoSave = useAutoSave<ISizingStateDTO>({
        debounceMs: 1500,
        maxRetries: 3,
        // A 403 refusal must reach the person losing the edits (ESCLOUD-288).
        onError: notifyWorkflowSaveRefused,
        getState: () => {
            const state = sizingService.serializeState();
            if (!carmViewService) return state;

            const { edits, custom, nextCustomId } = carmViewService.serializeCarmViews();
            if (edits.length === 0 && custom.length === 0) return state;

            return {
                ...state,
                carmViewEdits: edits.length > 0 ? edits : undefined,
                carmViewCustom: custom.length > 0 ? custom : undefined,
                carmViewNextCustomId: nextCustomId > 0 ? nextCustomId : undefined,
            };
        },
        saveFn: async (state, version) => {
            const response = await api.workflowState.upsert(caseId, WORKFLOW_STATE_KEY, {
                state,
                version,
            });
            return { version: response.version };
        },
    });

    // ── Restore state from API after sizing initialization ──
    useEffect(() => {
        const restoreFromApi = async () => {
            if (isRestoredRef.current) return;
            isRestoredRef.current = true;

            try {
                const saved = await api.workflowState.getByKey(caseId, WORKFLOW_STATE_KEY);
                if (saved) {
                    autoSave.setVersion(saved.version);
                    const dto = saved.state as ISizingStateDTO;
                    sizingService.loadState(dto);

                    if (carmViewService && (dto.carmViewEdits || dto.carmViewCustom)) {
                        carmViewService.loadCarmViews(
                            dto.carmViewEdits ?? [],
                            dto.carmViewCustom ?? [],
                            dto.carmViewNextCustomId ?? 0
                        );
                    }
                }
            } catch (error) {
                if (error instanceof ApiError && error.status === 404) {
                    return;
                }
                console.error('[SizingPersistence] Failed to restore state:', error);
                setRestoreError(error instanceof Error ? error : new Error(String(error)));
            }
        };

        const unsubscribe = sizingService.subscribe(sizingService.EVENTS.SIZING_INITIALIZED, restoreFromApi);

        // If sizing is already initialized (hook mounted after init), restore immediately
        if (sizingService.getTemplate()) {
            restoreFromApi();
        }

        return () => {
            unsubscribe();
            isRestoredRef.current = false;
        };
    }, [caseId, sizingService, carmViewService]);

    // ── Subscribe to user-mutation events -> markDirty ──
    useEffect(() => {
        const events = [
            sizingService.EVENTS.KEYPOINT_MOVED,
            sizingService.EVENTS.KEYPOINT_RESET,
            sizingService.EVENTS.DIAMETER_EDITED,
            sizingService.EVENTS.DIAMETER_RESTORED,
            sizingService.EVENTS.MEASUREMENT_RELOCATED,
            sizingService.EVENTS.CUSTOM_MEASUREMENT_ADDED,
            sizingService.EVENTS.CUSTOM_MEASUREMENT_REMOVED,
            sizingService.EVENTS.CUSTOM_MEASUREMENT_RENAMED,
            sizingService.EVENTS.OFFSET_ADDED,
            sizingService.EVENTS.OFFSET_REMOVED,
        ];

        const unsubscribers = events.map((event) => sizingService.subscribe(event, autoSave.markDirty));

        return () => {
            unsubscribers.forEach((unsub) => unsub());
        };
    }, [sizingService, autoSave.markDirty]);

    // ── Subscribe to C-arm view mutation events -> markDirty ──
    useEffect(() => {
        if (!carmViewService) return;

        const events = [
            carmViewService.EVENTS.VIEW_UPDATED,
            carmViewService.EVENTS.VIEW_ADDED,
            carmViewService.EVENTS.VIEW_DELETED,
            carmViewService.EVENTS.VIEW_RENAMED,
            carmViewService.EVENTS.VIEW_RESET,
        ];

        const unsubscribers = events.map((event) => carmViewService.subscribe(event, autoSave.markDirty));

        return () => {
            unsubscribers.forEach((unsub) => unsub());
        };
    }, [carmViewService, autoSave.markDirty]);

    return {
        status: autoSave.status,
        saveNow: autoSave.saveNow,
        conflict: autoSave.conflict,
        dismissConflict: autoSave.dismissConflict,
        lastSavedAt: autoSave.lastSavedAt,
        retryCount: autoSave.retryCount,
        restoreError,
    };
}
